// Package Imports
import { useEffect } from 'react'
import { Outlet, useNavigate, useSearch } from '@tanstack/react-router'
import { Space } from 'antd'
import { motion } from 'motion/react'
import { RiMoonClearLine, RiSparkling2Line } from 'react-icons/ri'

//  Core Imports
import { getAuth } from '@/core/utils/auth-utils'
import { useTheme } from '@/core/hooks/use-theme'
import { ROUTES } from '@/core/constants/routes'

// Module Imports
import Header from '@/modules/auth/components/header'
import Footer from '@/modules/auth/components/footer'

const AuthLayout = () => {
  const navigate = useNavigate()
  const search = useSearch({ strict: false })
  const { theme, toggleTheme } = useTheme()
  const isCanli = theme === 'canli'

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      const token = (search as { token?: unknown })?.token
      if (
        search.status === 'requested' ||
        search.status === 'signedOut' ||
        token
      )
        return
      const auth = await getAuth()
      if (cancelled) return
      if (auth?.email) navigate({ to: ROUTES.dashboard })
    }
    void run()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-linear-to-br from-indigo-500/10 to-purple-600/10 p-6">
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-linear-to-br from-indigo-500/10 to-purple-600/10" />
      <div className="absolute -bottom-36 -left-36 h-96 w-96 rounded-full bg-linear-to-br from-indigo-500/10 to-purple-600/10" />

      <motion.button
        type="button"
        onClick={(event) =>
          toggleTheme({ clientX: event.clientX, clientY: event.clientY })
        }
        data-testid="theme-toggle"
        aria-label="Tema değiştir"
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        whileTap={{ scale: 0.9, rotate: -8 }}
        whileHover={{ scale: 1.06 }}
        className={
          isCanli
            ? 'fixed top-4 right-4 z-10 inline-flex h-10 cursor-pointer items-center gap-2 overflow-hidden rounded-full border border-[var(--app-border)] bg-gradient-to-r from-[#7c5cff] via-[#22d3ee] to-[#ec4899] px-4 text-xs font-semibold text-white shadow-[0_8px_24px_-8px_rgba(124,92,255,0.7)] sm:top-6 sm:right-6'
            : 'fixed top-4 right-4 z-10 inline-flex h-10 cursor-pointer items-center gap-2 rounded-full border border-gray-200 bg-white px-4 text-xs font-semibold text-gray-700 shadow-lg hover:bg-gray-50 sm:top-6 sm:right-6'
        }
      >
        <motion.span
          key={isCanli ? 'canli' : 'klasik'}
          initial={{ rotate: -180, opacity: 0, scale: 0.5 }}
          animate={{ rotate: 0, opacity: 1, scale: 1 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="inline-flex items-center gap-2"
        >
          {isCanli ? (
            <>
              <RiSparkling2Line className="h-4 w-4" />
              Canlı
            </>
          ) : (
            <>
              <RiMoonClearLine className="h-4 w-4" />
              Klasik
            </>
          )}
        </motion.span>
      </motion.button>

      <div className="relative w-full max-w-lg rounded-2xl border-0 bg-white p-4 shadow-lg sm:p-8 sm:shadow-2xl">
        <Space orientation="vertical" size={24} className="w-full">
          <Header />
          <Outlet />
          <Footer />
        </Space>
      </div>
    </div>
  )
}

export default AuthLayout
